import { useEffect } from "react";
import { FormProvider } from "react-hook-form";
import { UserEnum } from "@/__generated__/graphql";
import { usePathname } from "@/components/hooks/use-pathname";
import { useRoute } from "@/components/shared/router";
import { Button, View } from "@/components/ui";
import { ensurePhoneNumberWithPlus } from "@/lib/helpers/string";
import { PosthogAnalytics } from "@/lib/monitoring/posthog";
import { useAuthState } from "@/store/auth";
import { useLoginForm } from "@/store/auth/forms/useLoginForm";
import { useSendPinCodeToPhoneStore } from "@/store/auth/sendPinCodeToPhone";
import { AuthModals, LoginType } from "@/types/users";

import { Divider } from "../../divider";
import { FormInputPhoneNumber } from "../../input-phone-number/controlled";
import { TextSeparator } from "../../text-separator/TextSeparator";
import { Typography } from "../../typography";
import { ErrorMessage } from "../error-message";

export const LoginContent = ({ isModal = true }: { isModal?: boolean }) => {
  const { replace } = useRoute();
  const pathname = usePathname();
  const { setPhone, setType, showModal, setLoginByModal } = useAuthState();
  const { loading, fetch: sendPinCode, error } = useSendPinCodeToPhoneStore();

  const loginFormMethods = useLoginForm();
  const {
    defaultValues,
    reset,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = loginFormMethods;

  useEffect(() => {
    setLoginByModal(isModal);
  }, []);

  const onSubmitForm = (data: { phoneNumber: string }) => {
    setType(LoginType.Login);
    const phone = ensurePhoneNumberWithPlus(data.phoneNumber);
    setPhone(phone);
    sendPinCode(
      {
        requestPayload: {
          input: {
            phone: phone,
            enableWhatsappOtp: true,
            userType: UserEnum.Client,
          },
        },
      },
      { selfHandleError: true },
    ).then((res) => {
      if (res.data?.success) {
        showModal(AuthModals.OTP);
      }
    });
  };
  const onGoToSignUp = () => {
    reset(defaultValues);
    PosthogAnalytics.track("user_sign_up", { path: pathname });
    if (isModal) showModal(AuthModals.SIGN_UP);
    else replace({ pageKey: "signup" });
  };

  return (
    <FormProvider {...loginFormMethods}>
      <View className="flex flex-col gap-2">
        <View className="flex flex-col gap-1">
          <Typography variant="label-md">Sign In</Typography>
          <Typography variant="body-sm" color="foreground-intermediate">
            To continue the booking process, you need to login or create a new account
          </Typography>
        </View>
        <Divider className="mb-4 mt-5" />
        <FormInputPhoneNumber name="phoneNumber" label="Phone Number" />
        {error && <ErrorMessage errorMessage={error} className="mt-4" />}
        <Button
          variant="primary"
          color="CTA"
          rounded="full"
          onClick={handleSubmit(onSubmitForm)}
          children="Sign In"
          className="mt-2"
          loading={loading}
          disabled={!isValid}
        />
        <View className="my-2">
          <TextSeparator label="Don't have an account?" />
        </View>
        <Button
          variant="secondary"
          color="CTA2"
          rounded="full"
          onClick={onGoToSignUp}
          disabled={isSubmitting}
          children="Sign Up"
        />
      </View>
    </FormProvider>
  );
};
